'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

const supabase = createClient();

// ─── TYPES ────────────────────────────────────────────────────────────────────
interface QueueStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  byCategory: Record<string, number>;
}

interface PollLog {
  id: string;
  source_name: string;
  source_domain: string;
  articles_found: number;
  articles_extracted: number;
  articles_inserted: number;
  articles_skipped: number;
  status: string;
  error_message: string | null;
  polled_at: string;
}

interface QueueArticle {
  id: string;
  source_name: string;
  article_title: string;
  article_url: string;
  article_summary: string;
  interest_category: string;
  relevance_score: number;
  relevance_reason: string;
  tags: string[];
  status: string;
  fetched_at: string;
}

const CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
  'marriage-family': { label: 'Marriage & Family', color: '#c47a7a' },
  'history-heritage': { label: 'History & Heritage', color: '#c9a84c' },
  'legacy-profiles': { label: 'Legacy & Profiles', color: '#8a8ab8' },
  'youth-young-adults': { label: 'Youth & Young Adults', color: '#2d8a87' },
  'faith-ramadan': { label: 'Faith & Ramadan', color: '#D4A82A' },
  'justice-civic': { label: 'Justice & Civic Life', color: '#2d7a47' },
};

const VERIFIED_SOURCES = [
  { name: 'MANA Justice', domain: 'manajustice.org', type: 'partner' },
  { name: 'Muslim American Society', domain: 'muslimamericansociety.org', type: 'partner' },
  { name: 'ICNA', domain: 'icna.org', type: 'partner' },
  { name: 'Zakat Foundation', domain: 'zakat.org', type: 'partner' },
  { name: 'CIOGC', domain: 'ciogc.org', type: 'partner' },
  { name: 'ISNA', domain: 'isna.net', type: 'partner' },
  { name: 'Sapelo Square', domain: 'sapelo.org', type: 'publication' },
  { name: 'Black Muslim Psychology', domain: 'blackmuslimpsychology.com', type: 'publication' },
  { name: 'Yaqeen Institute', domain: 'yaqeeninstitute.org', type: 'scholarly' },
  { name: 'Zaytuna College', domain: 'zaytuna.edu', type: 'scholarly' },
  { name: 'Muslim Matters', domain: 'muslimmatters.org', type: 'news' },
  { name: 'About Islam', domain: 'aboutislam.net', type: 'news' },
  { name: 'Sound Vision', domain: 'soundvision.com', type: 'news' },
  { name: 'IlmFeed', domain: 'ilmfeed.com', type: 'news' },
  { name: 'The Muslim Vibe', domain: 'themuslimvibe.com', type: 'news' },
  { name: 'Muslim Link', domain: 'muslimlink.ca', type: 'news' },
];

const SOURCE_TYPE_COLORS: Record<string, string> = {
  partner: '#c9a84c',
  publication: '#8a8ab8',
  scholarly: '#2d8a87',
  news: '#2d7a47',
};

// ─── MANUAL TRIGGER PANEL ─────────────────────────────────────────────────────
function ManualTriggerPanel() {
  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [logs, setLogs] = useState<string[]>([]);
  const [summary, setSummary] = useState<{ inserted: number; failed: number; successRate: string } | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const addLog = (msg: string) => {
    setLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const triggerFeed = async () => {
    setStatus('running');
    setLogs([]);
    setSummary(null);
    addLog('▶ Triggering MANA newsletter feed Edge Function...');

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('NEXT_PUBLIC_SUPABASE_URL not set');

      const fnUrl = `${supabaseUrl}/functions/v1/mana-newsletter-feed`;
      addLog(`→ POST ${fnUrl}`);

      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
      };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      addLog('→ Connecting to Edge Function...');
      const res = await fetch(fnUrl, { method: 'POST', headers, body: JSON.stringify({ manual: true }) });

      addLog(`→ Response status: ${res.status} ${res.statusText}`);

      if (!res.ok) {
        const errText = await res.text();
        addLog(`✗ Error: ${errText}`);
        setStatus('error');
        return;
      }

      const result = await res.json();
      addLog('✓ Edge Function completed successfully');

      if (result?.summary) {
        const s = result.summary;
        addLog(`→ Sources polled: ${s.sourcesPolled ?? '—'}`);
        addLog(`→ Articles found: ${s.articlesFound ?? '—'}`);
        addLog(`→ Articles inserted: ${s.articlesInserted ?? '—'}`);
        addLog(`→ Articles skipped: ${s.articlesSkipped ?? '—'}`);
        addLog(`→ Success rate: ${s.successRate ?? '—'}`);
        if (s.categoryBreakdown) {
          addLog('→ Category breakdown:');
          Object.entries(s.categoryBreakdown).forEach(([cat, count]) => {
            addLog(`   ${cat}: ${count}`);
          });
        }
        setSummary({
          inserted: s.articlesInserted ?? 0,
          failed: (s.sourcesPolled ?? 0) - (s.sourcesSucceeded ?? 0),
          successRate: s.successRate ?? '—',
        });
      } else if (result?.message) {
        addLog(`→ ${result.message}`);
      } else {
        addLog(`→ Raw response: ${JSON.stringify(result).slice(0, 200)}`);
      }

      setStatus('done');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog(`✗ Exception: ${msg}`);
      setStatus('error');
    }
  };

  const statusColors: Record<string, string> = {
    idle: 'rgba(212,197,169,0.4)',
    running: '#c9a84c',
    done: '#2d7a47',
    error: '#c47a7a',
  };

  const statusLabels: Record<string, string> = {
    idle: 'Ready',
    running: 'Running…',
    done: 'Complete',
    error: 'Error',
  };

  return (
    <div style={{
      background: 'rgba(6,13,18,0.98)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 10,
      padding: '24px 28px',
      marginBottom: 32,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', margin: '0 0 4px' }}>
            ◈ On-Demand Feed Trigger
          </p>
          <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: '#F5F0E8', margin: 0, fontWeight: 400 }}>
            Run MANA Newsletter Feed
          </h3>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', margin: '4px 0 0', letterSpacing: '.04em' }}>
            Polls all 16 verified sources, extracts real articles via RSS, categorizes with Claude, and populates the queue
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Status badge */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 12px',
            background: `${statusColors[status]}10`,
            border: `1px solid ${statusColors[status]}30`,
            borderRadius: 20,
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%',
              background: statusColors[status],
              animation: status === 'running' ? 'pulse 1s infinite' : 'none',
            }} />
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: statusColors[status], letterSpacing: '.1em', textTransform: 'uppercase' }}>
              {statusLabels[status]}
            </span>
          </div>
          {/* Trigger button */}
          <button
            onClick={triggerFeed}
            disabled={status === 'running'}
            style={{
              fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
              padding: '10px 20px', borderRadius: 6, cursor: status === 'running' ? 'not-allowed' : 'pointer',
              background: status === 'running' ? 'rgba(201,168,76,0.05)' : 'rgba(201,168,76,0.12)',
              border: `1px solid ${status === 'running' ? 'rgba(201,168,76,0.15)' : 'rgba(201,168,76,0.4)'}`,
              color: status === 'running' ? 'rgba(201,168,76,0.4)' : '#c9a84c',
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            {status === 'running' ? (
              <>
                <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>◌</span>
                Running…
              </>
            ) : (
              <>▶ Trigger Feed Now</>
            )}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ padding: '10px 16px', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 6 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 18, color: '#2d7a47', fontWeight: 700 }}>{summary.inserted}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Articles Added</div>
          </div>
          <div style={{ padding: '10px 16px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 18, color: '#c9a84c', fontWeight: 700 }}>{summary.successRate}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Success Rate</div>
          </div>
          {summary.failed > 0 && (
            <div style={{ padding: '10px 16px', background: 'rgba(196,122,122,0.06)', border: '1px solid rgba(196,122,122,0.15)', borderRadius: 6 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 18, color: '#c47a7a', fontWeight: 700 }}>{summary.failed}</div>
              <div style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Sources Failed</div>
            </div>
          )}
        </div>
      )}

      {/* Live log output */}
      {logs.length > 0 && (
        <div style={{
          background: '#040a0f',
          border: '1px solid rgba(201,168,76,0.1)',
          borderRadius: 6,
          padding: '12px 14px',
          maxHeight: 200,
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: 10,
          lineHeight: 1.7,
          color: 'rgba(212,197,169,0.6)',
        }}>
          {logs.map((log, i) => (
            <div key={i} style={{
              color: log.includes('✓') ? '#2d7a47' : log.includes('✗') ? '#c47a7a' : log.includes('▶') ? '#c9a84c' : 'rgba(212,197,169,0.55)',
            }}>
              {log}
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function MANAFeedStatus() {
  const [activeTab, setActiveTab] = useState<'queue' | 'sources' | 'logs'>('queue');
  const [queueStats, setQueueStats] = useState<QueueStats>({ total: 0, pending: 0, approved: 0, rejected: 0, byCategory: {} });
  const [pollLogs, setPollLogs] = useState<PollLog[]>([]);
  const [queueArticles, setQueueArticles] = useState<QueueArticle[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [categoryFilter]);

  async function loadData() {
    setLoading(true);
    try {
      // Load queue stats
      const { data: queueData } = await supabase
        .from('newsletter_queue')
        .select('status, interest_category');

      if (queueData) {
        const s: QueueStats = {
          total: queueData.length,
          pending: queueData.filter(a => a.status === 'pending').length,
          approved: queueData.filter(a => a.status === 'approved').length,
          rejected: queueData.filter(a => a.status === 'rejected').length,
          byCategory: {},
        };
        for (const a of queueData) {
          s.byCategory[a.interest_category] = (s.byCategory[a.interest_category] || 0) + 1;
        }
        setQueueStats(s);
      }

      // Load articles
      let query = supabase
        .from('newsletter_queue')
        .select('*')
        .order('fetched_at', { ascending: false })
        .limit(50);

      if (categoryFilter !== 'all') query = query.eq('interest_category', categoryFilter);

      const { data: articleData } = await query;
      if (articleData) setQueueArticles(articleData as QueueArticle[]);

      // Load poll logs
      const { data: logData } = await supabase
        .from('newsletter_poll_log')
        .select('*')
        .order('polled_at', { ascending: false })
        .limit(30);
      if (logData) setPollLogs(logData as PollLog[]);
    } catch (err) {
      console.error('Failed to load MANA feed data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function updateArticleStatus(id: string, status: 'approved' | 'rejected') {
    await supabase
      .from('newsletter_queue')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id);
    setQueueArticles(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    if (queueStats) {
      setQueueStats(prev => prev ? {
        ...prev,
        pending: prev.pending - 1,
        approved: status === 'approved' ? prev.approved + 1 : prev.approved,
        rejected: status === 'rejected' ? prev.rejected + 1 : prev.rejected,
      } : null);
    }
  }

  return (
    <div style={{ background: '#060d12', padding: '48px 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 32px' }}>

        {/* Manual trigger panel */}
        <ManualTriggerPanel />

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <p style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em',
            textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 8,
          }}>
            ◈ MANA Newsletter · RSS Intelligence
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h2 style={{
                fontFamily: "'Amiri', serif", fontSize: 32, color: '#F5F0E8',
                fontWeight: 400, marginBottom: 8, lineHeight: 1.2,
              }}>
                Content Feed — 16 Verified Sources
              </h2>
              <p style={{
                fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,197,169,0.55)',
                lineHeight: 1.7, maxWidth: 600,
              }}>
                Anthropic-powered hourly polling of MANA partner organizations, African-American Muslim publications, and Islamic scholarly sources. Articles extracted by interest category and queued for newsletter distribution.
              </p>
            </div>
          </div>
        </div>

        {/* Stats row */}
        {queueStats && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 28,
          }}>
            {[
              { label: 'Total Articles', value: queueStats.total, color: '#c9a84c' },
              { label: 'Pending Review', value: queueStats.pending, color: '#D4A82A' },
              { label: 'Approved', value: queueStats.approved, color: '#2d7a47' },
              { label: 'Rejected', value: queueStats.rejected, color: '#c47a7a' },
            ].map((s) => (
              <div key={s.label} style={{
                padding: '14px 16px',
                background: 'rgba(6,13,18,0.8)',
                border: `1px solid ${s.color}20`,
                borderRadius: 4,
              }}>
                <div style={{
                  fontFamily: "'Amiri', serif", fontSize: 28, color: s.color,
                  fontWeight: 400, lineHeight: 1, marginBottom: 4,
                }}>
                  {s.value}
                </div>
                <div style={{
                  fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
                  textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)',
                }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid rgba(201,168,76,0.1)', paddingBottom: 0 }}>
          {(['queue', 'sources', 'logs'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em',
                textTransform: 'uppercase', padding: '10px 16px',
                background: 'none', cursor: 'pointer',
                border: 'none',
                borderBottom: `2px solid ${activeTab === tab ? '#c9a84c' : 'transparent'}`,
                color: activeTab === tab ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                transition: 'all 0.15s',
              }}
            >
              {tab === 'queue' ? `◈ Article Queue (${queueStats?.pending || 0} pending)` : tab === 'sources' ? `◈ 16 Sources` : '◈ Poll Logs'}
            </button>
          ))}
        </div>

        {/* QUEUE TAB */}
        {activeTab === 'queue' && (
          <div>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em',
                  background: '#0a1018', border: '1px solid rgba(201,168,76,0.2)',
                  color: 'rgba(212,197,169,0.7)', padding: '6px 10px', borderRadius: 3,
                  cursor: 'pointer',
                }}
              >
                <option value="all">All Categories</option>
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>

            {loading ? (
              <div style={{ padding: '40px 0', textAlign: 'center', fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em' }}>
                Loading articles...
              </div>
            ) : queueArticles.length === 0 ? (
              <div style={{
                padding: '40px 24px', textAlign: 'center',
                border: '1px dashed rgba(201,168,76,0.15)', borderRadius: 4,
              }}>
                <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.35)', letterSpacing: '.1em', marginBottom: 8 }}>
                  No articles in queue yet
                </p>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.4)' }}>
                  Trigger a poll to fetch articles from the 16 verified MANA partner sources.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {queueArticles.map((article) => {
                  const catCfg = CATEGORY_CONFIG[article.interest_category] || { label: article.interest_category, color: '#c9a84c' };
                  return (
                    <div
                      key={article.id}
                      style={{
                        padding: '16px 18px',
                        background: '#0a1018',
                        border: `1px solid ${article.status === 'approved' ? 'rgba(45,122,71,0.2)' : article.status === 'rejected' ? 'rgba(196,122,122,0.15)' : 'rgba(201,168,76,0.1)'}`,
                        borderRadius: 4,
                        display: 'flex', gap: 16, alignItems: 'flex-start',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                          <span style={{
                            fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
                            textTransform: 'uppercase', color: catCfg.color,
                            padding: '2px 6px', border: `1px solid ${catCfg.color}30`,
                            borderRadius: 2, background: `${catCfg.color}08`,
                          }}>
                            {catCfg.label}
                          </span>
                          <span style={{
                            fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.35)',
                            letterSpacing: '.06em',
                          }}>
                            {article.source_name} · Score: {article.relevance_score}
                          </span>
                          {article.tags?.slice(0, 3).map((tag) => (
                            <span key={tag} style={{
                              fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.25)',
                              padding: '1px 5px', border: '1px solid rgba(212,197,169,0.1)',
                              borderRadius: 2,
                            }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                        <a
                          href={article.article_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontFamily: "'Georgia', serif", fontSize: 14, color: '#F5F0E8',
                            textDecoration: 'none', lineHeight: 1.4, display: 'block', marginBottom: 6,
                          }}
                        >
                          {article.article_title}
                        </a>
                        {article.article_summary && (
                          <p style={{
                            fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.55)',
                            lineHeight: 1.65, margin: '0 0 6px',
                          }}>
                            {article.article_summary}
                          </p>
                        )}
                        {article.relevance_reason && (
                          <p style={{
                            fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.35)',
                            letterSpacing: '.04em', margin: 0, fontStyle: 'italic',
                          }}>
                            {article.relevance_reason}
                          </p>
                        )}
                      </div>
                      {article.status === 'pending' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                          <button
                            onClick={() => updateArticleStatus(article.id, 'approved')}
                            style={{
                              fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
                              textTransform: 'uppercase', padding: '5px 10px',
                              background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)',
                              color: '#2d7a47', borderRadius: 3, cursor: 'pointer',
                            }}
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => updateArticleStatus(article.id, 'rejected')}
                            style={{
                              fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
                              textTransform: 'uppercase', padding: '5px 10px',
                              background: 'rgba(196,122,122,0.08)', border: '1px solid rgba(196,122,122,0.2)',
                              color: '#c47a7a', borderRadius: 3, cursor: 'pointer',
                            }}
                          >
                            ✕ Reject
                          </button>
                        </div>
                      )}
                      {article.status !== 'pending' && (
                        <div style={{
                          fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
                          textTransform: 'uppercase', padding: '5px 10px',
                          color: article.status === 'approved' ? '#2d7a47' : '#c47a7a',
                          border: `1px solid ${article.status === 'approved' ? 'rgba(45,122,71,0.2)' : 'rgba(196,122,122,0.2)'}`,
                          borderRadius: 3, flexShrink: 0,
                        }}>
                          {article.status}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SOURCES TAB */}
        {activeTab === 'sources' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {VERIFIED_SOURCES.map((source) => {
              const lastLog = pollLogs.find(l => l.source_domain === source.domain);
              const typeColor = SOURCE_TYPE_COLORS[source.type] || '#c9a84c';
              return (
                <div
                  key={source.domain}
                  style={{
                    padding: '14px 16px',
                    background: '#0a1018',
                    border: `1px solid ${typeColor}15`,
                    borderLeft: `3px solid ${typeColor}40`,
                    borderRadius: 4,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div>
                      <p style={{
                        fontFamily: "'Georgia', serif", fontSize: 13, color: '#F5F0E8',
                        margin: '0 0 3px', fontWeight: 400,
                      }}>
                        {source.name}
                      </p>
                      <p style={{
                        fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.35)',
                        letterSpacing: '.06em', margin: 0,
                      }}>
                        {source.domain}
                      </p>
                    </div>
                    <span style={{
                      fontFamily: 'monospace', fontSize: 6, letterSpacing: '.1em',
                      textTransform: 'uppercase', color: typeColor,
                      padding: '2px 6px', border: `1px solid ${typeColor}30`,
                      borderRadius: 2, background: `${typeColor}08`,
                    }}>
                      {source.type}
                    </span>
                  </div>
                  {lastLog ? (
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: lastLog.status === 'success' ? '#2d7a47' : '#c47a7a', letterSpacing: '.06em' }}>
                        {lastLog.status === 'success' ? '✓' : '✗'} {lastLog.status}
                      </span>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.04em' }}>
                        +{lastLog.articles_inserted} added
                      </span>
                      <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.25)', letterSpacing: '.04em' }}>
                        {new Date(lastLog.polled_at).toLocaleDateString()}
                      </span>
                    </div>
                  ) : (
                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.25)', letterSpacing: '.06em' }}>
                      Not yet polled
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* LOGS TAB */}
        {activeTab === 'logs' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pollLogs.length === 0 ? (
              <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em' }}>
                No poll logs yet — trigger a poll to see results
              </div>
            ) : pollLogs.map((log) => (
              <div
                key={log.id}
                style={{
                  padding: '10px 14px',
                  background: '#0a1018',
                  border: `1px solid ${log.status === 'success' ? 'rgba(45,122,71,0.15)' : 'rgba(196,122,122,0.15)'}`,
                  borderRadius: 3,
                  display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
                }}
              >
                <span style={{
                  fontFamily: 'monospace', fontSize: 8, color: log.status === 'success' ? '#2d7a47' : '#c47a7a',
                  letterSpacing: '.08em', minWidth: 60,
                }}>
                  {log.status === 'success' ? '✓' : '✗'} {log.status}
                </span>
                <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#F5F0E8', flex: 1 }}>
                  {log.source_name}
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', letterSpacing: '.06em' }}>
                  found: {log.articles_found} · extracted: {log.articles_extracted} · added: {log.articles_inserted} · skipped: {log.articles_skipped}
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.25)', letterSpacing: '.04em' }}>
                  {new Date(log.polled_at).toLocaleString()}
                </span>
                {log.error_message && (
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: '#c47a7a', letterSpacing: '.04em', width: '100%' }}>
                    Error: {log.error_message}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
